/**
 * UNA CARPETA POR LIBRO (R0-7, 27-sep-2026).
 *
 * Si en la carpeta del proyecto abierto hay más de un libro, se dice cuáles y se ofrece separar cada
 * uno de los que no son el abierto en su propio proyecto. Nunca se niega nada: el Studio sigue
 * trabajando igual con los dos libros juntos (informes, plan y versiones ya van por libro); esto es
 * para que el autor no acabe abriendo el que no era.
 *
 * Separar es COPIAR y se dice antes de hacerlo: qué se copia, adónde, y que su carpeta no se toca.
 * Un motor anterior no sabe contar los libros: no sale nada.
 */
import { useEffect, useState } from "react";
import { librosDeCarpeta, separarLibro } from "../api";
import type { LibroDeCarpeta, Separado } from "../api";
import { nombreDeRuta } from "../logica";

export default function LibrosEnCarpeta({ carpeta, doc, alSeparado, alAbrir }: {
  carpeta: string;
  doc: string | null;
  /** Tras separar: la lista de proyectos tiene uno más. */
  alSeparado?: () => void;
  /** Abrir el proyecto del libro, si el autor lo pide. */
  alAbrir?: (p: { id: string; carpeta: string }) => void;
}) {
  const [libros, setLibros] = useState<LibroDeCarpeta[] | null>(null);
  const [preguntando, setPreguntando] = useState<LibroDeCarpeta | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [hecho, setHecho] = useState<Separado | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  const [vuelta, setVuelta] = useState(0);
  useEffect(() => { setHecho(null); setFallo(null); setPreguntando(null); }, [carpeta]);
  useEffect(() => {
    let vigente = true;
    librosDeCarpeta(carpeta).then((d) => { if (vigente) setLibros(d?.libros ?? null); }).catch(() => { if (vigente) setLibros(null); });
    return () => { vigente = false; };
  }, [carpeta, vuelta]);

  if (!libros || libros.length < 2) return null;
  const abiertoAhora = doc ? libros.find((l) => l.ficheros.includes(nombreDeRuta(doc))) ?? null : null;
  const versiones = (l: LibroDeCarpeta) => (l.versiones ? `${l.versiones} ${l.versiones === 1 ? "versión" : "versiones"}` : `${l.ficheros.length} ${l.ficheros.length === 1 ? "documento" : "documentos"}`);

  async function separar(l: LibroDeCarpeta) {
    setOcupado(true); setFallo(null);
    try {
      setHecho(await separarLibro(carpeta, l.libro));
      setPreguntando(null);
      alSeparado?.();
      setVuelta((v) => v + 1);     // ahora ese libro «ya tiene su proyecto»
    } catch (e) {
      setFallo((e as Error).message);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section className="carta">
      <h2>En esta carpeta hay {libros.length} libros<span className="sub">mejor, un proyecto por libro</span></h2>
      <p className="vacio">
        El Studio lleva por separado los informes, el plan y las versiones de cada uno, pero con los dos en la misma carpeta es
        fácil acabar abriendo el que no era. Puedes separar cada libro en su propio proyecto: se <b>copia</b>, y aquí no se mueve
        ni se borra nada.
      </p>
      <div className="issues">
        {libros.map((l) => (
          <div key={l.libro} className="issue">
            <p><b>{l.nombre}</b> <span className="vacio">· {versiones(l)} · la última, <span className="mono">{l.ultimo}</span></span></p>
            {l === abiertoAhora ? (
              <p className="vacio">Es el que tienes abierto.</p>
            ) : l.enProyecto ? (
              <>
                <p className="vacio">
                  Ya tiene su proyecto, «{l.enProyecto.nombre}». Lo que hay aquí es la copia de antes: cuando lo tengas claro,
                  bórrala tú de esta carpeta. El Studio no borra nada.
                </p>
                {alAbrir && <div className="botones"><button className="btn texto" onClick={() => alAbrir(l.enProyecto!)}>Abrir ese proyecto</button></div>}
              </>
            ) : preguntando?.libro === l.libro ? (
              <div className="aviso">
                <span>
                  Se crea el proyecto «{l.nombre}» en <b className="mono">~/HumanInk/Proyectos</b> (con un número detrás si ya tienes
                  uno que se llama así) y se copian sus {versiones(l)} y lo que el Studio guarda de ese libro. Al acabar te digo qué se
                  ha quedado aquí. Tu carpeta no se toca y sigues en el proyecto en el que estás.
                </span>
                <div className="botones">
                  <button className="btn" disabled={ocupado} onClick={() => separar(l)}>{ocupado ? "Copiando…" : "Copiar a su proyecto"}</button>
                  <button className="btn texto" disabled={ocupado} onClick={() => setPreguntando(null)}>Ahora no</button>
                </div>
              </div>
            ) : (
              <div className="botones">
                <button className="btn fantasma" onClick={() => { setPreguntando(l); setHecho(null); setFallo(null); }}>Separar en otro proyecto</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>No se ha podido separar: {fallo}</p>}
      {hecho && (
        <div className="aviso">
          <span>
            «{hecho.libro}» ya tiene su proyecto, «{hecho.proyecto.nombre}», con {hecho.documentos} {hecho.documentos === 1 ? "documento" : "documentos"} y lo que el
            Studio sabía de él, en <b className="mono">{hecho.proyecto.carpeta}</b>. Aquí sigue todo como estaba. Lo que no ha viajado:
          </span>
          {hecho.atras.map((a) => <p key={a} className="vacio" style={{ margin: "6px 0 0" }}>{a}</p>)}
          {alAbrir && <div className="botones"><button className="btn fantasma" onClick={() => alAbrir(hecho.proyecto)}>Abrir el proyecto nuevo</button></div>}
        </div>
      )}
    </section>
  );
}
